import { z } from 'zod';

const Day = z.iso.date();
const Localized = z.object({ uk: z.string(), en: z.string() });

/** A metric as the dashboard shows it (BB-132): title, formula in words and as counts, window. */
export const MetricDefinitionView = z.object({
  key: z.string(),
  title: Localized,
  channelScope: z.union([z.literal('all'), z.array(z.string())]),
  format: z.enum(['percent', 'count']),
  timeBasis: z.enum(['cohort_day', 'occurred_at']),
  formula: Localized,
  expression: z.string(),
  window: Localized.optional(),
});
export type MetricDefinitionView = z.infer<typeof MetricDefinitionView>;

/** Inclusive environment days; `cohort_day` metrics place facts on the send day of their dispatch. */
export const MetricQuery = z
  .object({
    from: Day,
    to: Day,
    campaignId: z.uuid().optional(),
    canvasId: z.uuid().optional(),
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 400, { message: 'at most 400 days', path: ['to'] });
export type MetricQuery = z.infer<typeof MetricQuery>;

export const MetricValueView = z.object({
  key: z.string(),
  from: Day,
  to: Day,
  value: z.number(),
  numerator: z.number(),
  denominator: z.number().nullable(),
  explanation: z.object({ code: z.literal('no_denominator'), uk: z.string(), en: z.string() }).optional(),
});
export type MetricValueView = z.infer<typeof MetricValueView>;

/**
 * Environment Home (BB-133, AN-009): app usage from session facts (BB-30) over inclusive environment days. `to`
 * defaults to today (environment time zone), `from` to `days` (default 30) days ending on `to`; `app` narrows to one
 * app instance.
 */
export const HOME_METRIC_KEYS = ['users', 'lifetime_sessions', 'mau', 'dau', 'new_users', 'stickiness', 'daily_sessions', 'sessions_per_mau'] as const;
export type HomeMetricKey = (typeof HOME_METRIC_KEYS)[number];

export const HomeQuery = z
  .object({ from: Day.optional(), to: Day.optional(), days: z.coerce.number().int().min(1).max(400).optional(), app: z.uuid().optional() })
  .refine((q) => !q.from || !q.to || q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => !q.from || !q.to || (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 400, { message: 'at most 400 days', path: ['to'] });
export type HomeQuery = z.infer<typeof HomeQuery>;

export interface HomeKpi {
  key: HomeMetricKey;
  title: { uk: string; en: string };
  formula: { uk: string; en: string };
  /** The formula as counts. */
  expression: string;
  format: 'count' | 'percent' | 'ratio';
  /** Null when it cannot be computed (no monthly active users in the period). */
  value: number | null;
}

export interface HomeDay {
  day: string;
  sessions: number;
  dau: number;
  /** Distinct users with a session in the 30 days ending this day. */
  mau: number;
  newUsers: number;
  stickiness: number | null;
  sessionsPerMau: number | null;
}

export interface HomeAppRow {
  appInstanceId: string;
  name: string;
  platform: string;
  users: number;
  sessions: number;
  dau: number;
  mau: number;
}

export interface HomeView {
  from: string;
  to: string;
  timezone: string;
  appInstanceId: string | null;
  /** Sessions recorded up to this moment are counted (aggregation watermark); null before the first run. */
  freshness: string | null;
  kpis: HomeKpi[];
  series: HomeDay[];
  byApp: HomeAppRow[];
}

/** «Pick up where you left off»: objects the employee viewed or changed in this environment, newest first. */
export const RECENT_OBJECT_TYPES = ['campaign', 'canvas', 'segment'] as const;
export interface RecentObjectView {
  objectType: (typeof RECENT_OBJECT_TYPES)[number];
  objectId: string;
  name: string;
  at: string;
  reason: 'viewed' | 'edited';
}

/**
 * Channel dashboards (BB-134, AN-002): the channel's registry metrics over inclusive environment send days
 * (`cohort_day`), optionally split by campaign category, campaign / Canvas or platform. `days` works as on Home.
 */
export const DASHBOARD_CHANNELS = ['push', 'content_card', 'in_app'] as const;
export const DASHBOARD_COMPARE = ['none', 'category', 'campaign', 'platform'] as const;
export const ChannelDashboardQuery = z
  .object({
    from: Day.optional(),
    to: Day.optional(),
    days: z.coerce.number().int().min(1).max(400).optional(),
    platform: z.enum(['android', 'ios', 'web']).optional(),
    /** Campaign category; Canvas messages count as marketing. */
    category: z.enum(['marketing', 'transactional']).optional(),
    /** Comma-separated campaign ids, at most 20. */
    campaignIds: z
      .string()
      .optional()
      .transform((v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : undefined))
      .pipe(z.array(z.uuid()).max(20).optional()),
    compareBy: z.enum(DASHBOARD_COMPARE).default('none'),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => !q.from || !q.to || (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 400, { message: 'at most 400 days', path: ['to'] });
export type ChannelDashboardQuery = z.infer<typeof ChannelDashboardQuery>;

export type DashboardMetricValue = Omit<MetricValueView, 'key' | 'from' | 'to'>;

export interface ChannelDashboardGroup {
  key: string;
  label: string;
  /** A campaign or Canvas group links to its page. */
  link: { type: 'campaign' | 'canvas'; id: string } | null;
  /** Over the whole period: uniques are distinct over the period, never sums of days. */
  totals: Record<string, DashboardMetricValue>;
  /** Per send day; a rate without a denominator that day is null. */
  series: Array<{ day: string; values: Record<string, number | null> }>;
}

export interface ChannelDashboardView {
  channel: (typeof DASHBOARD_CHANNELS)[number];
  from: string;
  to: string;
  timezone: string;
  compareBy: (typeof DASHBOARD_COMPARE)[number];
  metrics: MetricDefinitionView[];
  groups: ChannelDashboardGroup[];
  /** Campaign groups beyond the largest ones that were left out. */
  omittedGroups: number;
}
